import { Component, OnInit } from '@angular/core';
import { StripeService } from 'ngx-stripe';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalService } from '../shared/global.service';
import { WidgetService } from '../shared/api/widget/widget.service';
import { ToasterService } from '../shared/atoms/toaster/toaster.service';
import { CartService } from '../shared/cart/cart.service';
import { BookingShoppingCart } from '../shared/api/model/BookingShoppingCart';
import { StaticUtils } from '../shared/utils/static-utils';
import { Stripe } from '@stripe/stripe-js';

@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
})
export class PaymentComponent implements OnInit {
  creatingReservation: boolean = false;
  cart: BookingShoppingCart;
  elements: any;
  paymentElement: any;
  selectedPM: string;
  customerEmail: string;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _globalService: GlobalService,
    private _stripeService: StripeService,
    private _widgetService: WidgetService,
    private _cartService: CartService,
    private _toasterService: ToasterService
  ) { }

  async ngOnInit() {
    this._globalService.loading = true;

    if (!this._stripeService.stripe.getInstance()) {
      await new Promise((resolve) => {
        this._widgetService.getStripeKey().subscribe(
          (data) => {
            this._stripeService.setKey(data.stripeApiKey);
            resolve(true);
          },
          (error) => {
            this._toasterService.showError(error.error.message);
            resolve(false);
          }
        );
      });
    }
    this.loadCartAndInitPaymentRequest();
  }

  loadCartAndInitPaymentRequest() {
    setTimeout(async () => {
      this.cart = await this._cartService.getCart();
      this.customerEmail = this.cart && this.cart.bookings[0].customer.email;
      
      this.initExpressCheckout();
    });
  }

  initExpressCheckout() {
    const stripe = this._stripeService.stripe.getInstance();
    const totalAmount = Math.round(this.cart.billingTotal * 100);

    const options = {
      paymentMethodOrder: ['card', 'apple_pay', 'google_pay', 'multibanco'],
      layout: {
        type: 'accordion',
        defaultCollapsed: false,
        radios: true,
        spacedAccordionItems: false
      },
      fields: {
        billingDetails: {
          email: 'never'
        },
      }
    };

    this.elements = stripe.elements({
      mode: 'payment',
      amount: totalAmount,
      currency: 'eur',
      locale: 'en'
    });

    this.paymentElement = this.elements.create("payment", options);
    this.paymentElement.mount("#payment-element");

    this.paymentElement.on('change', (event: any) => {
      this.selectedPM = event.value?.type;
    });

    this._globalService.loading = false;
  }

  async handleSubmit(event: any) {
    event.preventDefault();
    let intentResult: any;
    const stripe = this._stripeService.stripe.getInstance() as Stripe;
    const guid = this._route.snapshot.queryParamMap.get('guid');

    this._globalService.loading = true;
    this.creatingReservation = true;
    this._globalService.processingPayment = true;

    if (!stripe || !this.elements) {
      this._globalService.errorMessage = 'Something went wrong with your connection. Please check your internet and try again.';

      this._router.navigate(['../error/'], {
        queryParams: {
          goHome: true,
        },
        queryParamsHandling: 'merge'
      });

      return;
    }

    const { error: submitError } = await this.elements.submit();

    if (submitError) {
      this._globalService.loading = false;
      this.creatingReservation = false;
      this._globalService.processingPayment = false;

      if (submitError.code === 'incomplete' && submitError.type === 'validation_error') {
        this._globalService.errorMessage = 'Something unexpected happened. Please try again';

        this._router.navigate(['../error/'], { queryParamsHandling: 'merge' });
      }
      return;
    }

    intentResult = await this._widgetService.shoppingCartStripePaymentIntent(this.cart.id, this.cart.guid).toPromise();
    
    const clientSecret = intentResult.clientSecret;
    const shoppingCartId = intentResult.shoppingCartId;
    const shoppingCartGuid = intentResult.shoppingCartGuid;
    this._globalService.booking = intentResult.bookingShoppingCart;

    if (intentResult?.isExpired) {
      this._globalService.loading = false;
      this.handleExpiration();
      
      return;
    }
    
    const expirationDate = intentResult?.bookingShoppingCart?.shoppingCartExpiration;

    if (expirationDate) {
      const localExpirationDate = new Date(expirationDate + 'Z');
      this._globalService.setExpirationDate(localExpirationDate);
    }

    const stripeAny = stripe as any;

    if (this.selectedPM === 'multibanco') {
      const { paymentIntent, error } = await stripeAny.confirmMultibancoPayment(
        clientSecret,
        { 
          payment_method: {
            type: 'multibanco', 
            billing_details: {
              email: this.customerEmail
            }
          }
        },
        { handleActions: false }
      );
      
      const isAtmPayment = paymentIntent.status === 'requires_action' && paymentIntent.next_action.type === "multibanco_display_details";
      
      if (error) return this.handleStripeErrors({ error });
      
      const multibancoDetails = paymentIntent.next_action!.multibanco_display_details;

      this._globalService.setMultibancoDetails({
        amount: paymentIntent.amount,
        reference: multibancoDetails.reference,
        entity: multibancoDetails.entity,
        hostedVoucherUrl: multibancoDetails.hosted_voucher_url,
        expiresAt: new Date(multibancoDetails.expires_at * 1000)
      });

      this._globalService.loading = false;
      this.creatingReservation = false;
      this._globalService.processingPayment = false;

      this.cart = await this._cartService.getCart();
      this._createAnalyticsEvent(this.cart);

      await this._router.navigate(['../multibanco'], 
            {   
              queryParams: { isAtmPayment },
              queryParamsHandling: 'merge',
            }, 
           );

      return;
    }

    const options = {
      elements: this.elements,
      clientSecret,
      confirmParams: {
        return_url: `${window.location.origin}/thankyou?guid=${guid}&scid=${shoppingCartId}&scguid=${shoppingCartGuid}`,
        payment_method_data: {
          billing_details: { 
            email: this.customerEmail
          }
        }
      },
      redirect: 'if_required'
    };

    const result = await stripeAny.confirmPayment(options, { handleActions: false});
    const isAtmPayment = result.paymentIntent && result.paymentIntent.status === 'requires_action' && result.paymentIntent.next_action.type === "multibanco_display_details";
    
    if (result.error) {
      this.handleStripeErrors(result);
    } else if (result && result.paymentIntent && result.paymentIntent.status === 'succeeded' || isAtmPayment) {
      this._globalService.loading = false;
      this.creatingReservation = false;
      this._globalService.processingPayment = false;

      this.cart = await this._cartService.getCart();
      this._createAnalyticsEvent(this.cart);

      await this._router.navigate(['../thankyou'], 
            {   
              queryParams: { isAtmPayment },
              queryParamsHandling: 'merge',
            }, 
           );
    }
  }

  handleExpiration(): void {
    this._globalService.stopTimer();
    this._router.navigate(['../timeExpiration'], { queryParamsHandling: 'merge' });
  }

  /**
  * Handles Stripe errors based on specific conditions to determine if the application should redirect to an error page.
  *
  * - payment_intent_authentication_failure: Occurs when an authentication step fails (e.g., 3D Secure authentication).
  * - processing_error: Indicates an error occurred during the processing of the payment.
  * - card_decline & card_error without shouldRetry: When a card is declined and the error type is card_error, but there's no option to retry.
  * 
  * If any of these conditions are met, the user is redirected to the '../error/' page for further resolution. 
  * Otherwise, the error is considered handled by the Payment Element component itself.
  */
  handleStripeErrors(result: any): void {
    const error = result?.error;
    this._globalService.loading = false;
    this._globalService.processingPayment = false;
    this.creatingReservation = false;

    if (error?.code === 'payment_intent_authentication_failure' ||
      error?.code === 'processing_error' ||
      (error?.code === 'card_declined' && error?.type === 'card_error' && !error?.shouldRetry) ||
      error?.type === 'invalid_request_error'
    ) {
      this._globalService.errorMessage = error.message;

      this._router.navigate(['../error/'], { queryParamsHandling: 'merge' });
    }
  }

  private _createAnalyticsEvent(cart: object): void {
    const products = [];

    cart['bookings'].forEach(booking => {
      products.push({
        'name': booking.event.product.name,
        'id': booking.event.product.id.toString(),
        'quantity': booking.paxTotal,
        'price': booking.billingTotal,
        'coupon': booking.attachPromocode,
      });
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'purchase',
      'ecommerce': {
        'currencyCode': 'EUR',
        'purchase': {
          'actionField': {
            'id': cart['id'],
            'affiliation': 'Pluralo Widget',
            'revenue': cart['billingTotal'],
            'coupon': cart['attachPromocode'],
          },
          products
        }
      }
    });
  }

  formatErrorMessage(message: string): string {
    return message.replace(/\n/g, '<br>');
  }

  get totalPrice(): string {
    return StaticUtils.convertValueToCurrency(this.cart && this.cart.billingTotal ? this.cart.billingTotal : 0);
  }
}
